import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate } from '@/utils/dateFormat'
import { useProjects, createProject, updateProject, deactivateProject } from '@/hooks/useProjects'
import { PageHeader } from '@/components/ui/PageHeader'
import { Project, ProjectListParams } from '@/types/index'
import { DataTable } from '@/components/ui/DataTable'
import { Modal } from '@/components/ui/Modal'
import { CompanySelect } from '@/components/ui/CompanySelect'
import { Search, AlertCircle } from 'lucide-react'
import { DEFAULT_PAGE_SIZE } from '@/api/paging'
import { useAuth } from '@/auth/useAuth'
import { UserRole } from '@/types'

// Bug #328: the API returns lowercase statuses ('active', 'completed',
// 'archived') and snake_case field names. The page previously assumed
// capitalised statuses and a `created` field, neither of which the backend
// has ever produced — so the Status filter matched nothing, every badge
// rendered as inactive, and the Created column was blank. Field names are
// now mapped in `api/projects.ts`; status casing is normalised here.
const STATUS_FILTERS = ['All', 'Active', 'Inactive', 'Archived'] as const
type StatusFilter = (typeof STATUS_FILTERS)[number]

const titleCase = (value?: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '-'

const isActiveStatus = (value?: string): boolean =>
  (value || '').toLowerCase() === 'active'

// Bug #334: `clientCompany` was a free-text string bound to a "Search for
// company..." input that searched nothing. `lab_project.client_id` is a NOT
// NULL FK, so the form now carries the id — `clientName` alongside it purely
// so the picker can render the selection without refetching the company.
//
// `sourceSystem` is gone: it was a free-text field on a create form that
// could never submit, and a hand-created project is recorded as 'manual' with
// a generated identifier by the backend now (see resolve_project_source).
// Leaving the box would invite someone to type 'hubspot' and collide with the
// deal sync's own rows.
const emptyForm = {
  name: '',
  description: '',
  clientId: '',
  clientName: '',
  status: 'Active',
  sourceUrl: '',
}

const ProjectList: React.FC = () => {
  const navigate = useNavigate()
  // Bug #370: projects arrive by integration (HubSpot deal sync), and create /
  // edit / deactivate are platform-admin only on the backend — so only offer
  // them to platform admins. Everyone else can still browse and view.
  const { user } = useAuth()
  const isPlatformAdmin = !!user?.roles?.includes(UserRole.PlatformAdmin)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All')
  const [page, setPage] = useState(1)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  // WI #337: changing the query resets the page, batched into the same render
  // so a single request goes out rather than one per state update.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // WI #337: memoised — `useProjects` re-fetches on this object's identity.
  // `GET /projects` takes `search` (name + description) and `status`, so both
  // controls now narrow the whole dataset rather than the loaded page.
  // Status values are stored lowercase (Bug #328), hence the toLowerCase.
  const listParams = useMemo<ProjectListParams>(
    () => ({
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(statusFilter !== 'All' ? { status: statusFilter.toLowerCase() } : {}),
    }),
    [page, debouncedSearch, statusFilter],
  )

  const { projects, total, isLoading, error, refetch } = useProjects(listParams)

  const closeModal = () => {
    setCreateModalOpen(false)
    setEditingProject(null)
    setFormError(null)
  }

  // Bug #334: this used to call the `createProject` stub in
  // `hooks/useProjects.ts` — `// API call stub`, `Promise.resolve()` — so the
  // modal closed as though it had saved and nothing was created. It also
  // passed `formData` straight through, whose `clientCompany` free-text field
  // the API has never accepted; the required `client_id` was never sent at
  // all, so a real POST would have been a 422.
  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setFormError('Project name is required')
      return
    }
    if (!formData.clientId) {
      setFormError('A client company is required')
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        clientId: formData.clientId,
        status: formData.status,
        sourceUrl: formData.sourceUrl.trim() || undefined,
      }
      if (editingProject) {
        await updateProject(editingProject.id, payload)
      } else {
        // No source_system / source_id: both are optional on create now and
        // the backend records a hand-created project as 'manual' with a
        // generated identifier (Bug #334, mirroring #331 on companies).
        await createProject(payload)
      }
      closeModal()
      setFormData(emptyForm)
      await refetch()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save project')
    } finally {
      setSaving(false)
    }
  }

  // Was `onClick: (_row) => {}` — a literal no-op behind a danger-styled menu
  // item. `deactivateProject` itself was already correct (Bug #328).
  const handleDeactivate = async (row: Project) => {
    setActionError(null)
    try {
      await deactivateProject(row.id)
      await refetch()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to deactivate project')
    }
  }

  const handleView = (projectId: string) => {
    navigate(`/projects/${projectId}`)
  }

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (value: string) => <div style={{ fontWeight: 500 }}>{value}</div>,
    },
    {
      key: 'clientName',
      header: 'Client company',
      // Bug #330: the API resolves the name server-side now. Rendered as
      // plain text rather than a link — the previous markup built
      // `/companies/<name>`, which was never a real route.
      render: (value?: string) => value || '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (value: string) => (
        <span
          style={{
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 600,
            backgroundColor: isActiveStatus(value) ? '#dbeafe' : '#f3f4f6',
            color: isActiveStatus(value) ? '#0284c7' : '#6b7280',
          }}
        >
          {titleCase(value)}
        </span>
      ),
    },
    { key: 'sourceSystem', header: 'Source', render: (value?: string) => value || '-' },
    {
      key: 'createdAt',
      header: 'Created',
      render: (value?: string) => formatDate(value),
    },
  ]

  return (
    <div style={{ padding: '0' }}>
      <PageHeader
        title="Projects"
        actions={
          isPlatformAdmin && (
            <button
              onClick={() => {
                setEditingProject(null)
                setFormError(null)
                setFormData(emptyForm)
                setCreateModalOpen(true)
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--color-magenta)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              + Add project
            </button>
          )
        }
      />

      <div style={{ padding: '24px' }}>
        {error && (
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '20px',
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
            }}
          >
            <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ margin: 0, color: '#dc2626', fontWeight: 500 }}>Error loading projects</p>
              <p style={{ margin: '4px 0 0 0', color: '#991b1b', fontSize: '13px' }}>{error?.message ?? 'An error occurred'}</p>
            </div>
          </div>
        )}

        {/* Bug #334: a failed Deactivate happens outside the modal, so it
            needs somewhere of its own to surface rather than failing silently. */}
        {actionError && (
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '20px',
              color: '#dc2626',
              fontSize: '13px',
            }}
          >
            {actionError}
          </div>
        )}

        {/* Search and filter bar */}
        <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '10px', top: '10px', color: 'var(--color-dark-grey)' }} />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
              }}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as 'All' | 'Active' | 'Inactive' | 'Archived')
              setPage(1)
            }}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'var(--color-white)',
              cursor: 'pointer',
            }}
          >
            <option value="All">All status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Archived">Archived</option>
          </select>
        </div>

        {/* Data table */}
        {isLoading && projects.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
            Loading projects...
          </div>
        ) : projects.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
            {searchTerm || statusFilter !== 'All' ? 'No projects match your search.' : 'No projects found.'}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={projects as Project[]}
            rowKey="id"
            isLoading={isLoading}
            totalItems={total}
            currentPage={page}
            pageSize={DEFAULT_PAGE_SIZE}
            onPageChange={setPage}
            // Bug #330: clicking anywhere in the row opens the project. The
            // actions menu stops propagation itself (DataTable, Bug #166), so
            // the View/Edit/Deactivate menu still works.
            onRowClick={(row: Project) => handleView(row.id)}
            actions={[
              { label: 'View', onClick: (row: Project) => handleView(row.id) },
              ...(isPlatformAdmin
                ? [
                  {
                    label: 'Edit',
                    onClick: (row: Project) => {
                      setEditingProject(row)
                      setFormError(null)
                      setFormData({
                        name: row.name,
                        description: row.description || '',
                        clientId: row.clientId || '',
                        clientName: row.clientName || '',
                        status: titleCase(row.status),
                        sourceUrl: row.sourceUrl || '',
                      })
                      setCreateModalOpen(true)
                    },
                  },
                  {
                    label: 'Deactivate',
                    onClick: (row: Project) => void handleDeactivate(row),
                    variant: 'danger' as const,
                  },
                ]
                : []),
            ]}
          />
        )}
      </div>

      {/* Create/Edit modal */}
      <Modal
          isOpen={createModalOpen}
          title={editingProject ? 'Edit project' : 'Add project'}
          onClose={closeModal}
          isDirty={formData.name !== '' || formData.description !== '' || formData.clientId !== '' || formData.sourceUrl !== ''}
          footer={
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={closeModal}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--color-white)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !formData.name.trim() || !formData.clientId}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--color-magenta)',
                  color: 'var(--color-white)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor:
                    saving || !formData.name.trim() || !formData.clientId
                      ? 'not-allowed'
                      : 'pointer',
                  opacity: saving || !formData.name.trim() || !formData.clientId ? 0.6 : 1,
                  fontFamily: 'var(--font-family)',
                }}
              >
                {saving ? 'Saving...' : editingProject ? 'Save' : 'Create'}
              </button>
            </div>
          }
        >
          <div style={{ display: 'grid', gap: '16px' }}>
            {formError && (
              <div
                style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  padding: '10px 12px',
                  color: '#dc2626',
                  fontSize: '13px',
                }}
              >
                {formError}
              </div>
            )}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Project name (required)
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  minHeight: '100px',
                  resize: 'vertical',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Client company (required)
              </label>
              <CompanySelect
                value={formData.clientId || undefined}
                valueName={formData.clientName || undefined}
                disabled={saving}
                onChange={(clientId, clientName) =>
                  setFormData({
                    ...formData,
                    clientId: clientId || '',
                    clientName: clientName || '',
                  })
                }
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  backgroundColor: 'var(--color-white)',
                  cursor: 'pointer',
                }}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Archived">Archived</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Source URL
              </label>
              <input
                type="text"
                value={formData.sourceUrl}
                onChange={(e) => setFormData({ ...formData, sourceUrl: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
          </div>
        </Modal>
    </div>
  )
}

export default ProjectList
